import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import api from "../../api";

const toInput = (date) => date.toISOString().slice(0, 10);

const weeksAgo = (weeks) => {
  const d = new Date();
  d.setDate(d.getDate() - weeks * 7);
  return d;
};

/**
 * Send the same term report window to every parent in a group.
 *
 * No per-student preview here — that lives on the student page. The send is queued server-side,
 * one job for the group, so the tutor is not left waiting on N model calls.
 */
const GroupProgressReportDialog = ({ open, group, recipientCount, onClose }) => {
  const [from, setFrom] = useState(toInput(weeksAgo(12)));
  const [to, setTo] = useState(toInput(new Date()));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const send = async () => {
    try {
      setSending(true);
      setError(null);
      const res = await api.post("tutor/progress-reports/send-group", {
        course_plan_id: group?.id,
        period_start: from,
        period_end: to,
      });
      toast.success(res.data?.message || "Progress reports queued.");
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || "Could not queue the reports.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Send progress reports
        <Typography variant="body2" color="text.secondary">
          {group?.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1.5} sx={{ mt: 1, mb: 1.5 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            label="Last 4 weeks"
            onClick={() => {
              setFrom(toInput(weeksAgo(4)));
              setTo(toInput(new Date()));
            }}
          />
          <Chip
            size="small"
            label="Last 12 weeks"
            onClick={() => {
              setFrom(toInput(weeksAgo(12)));
              setTo(toInput(new Date()));
            }}
          />
        </Stack>

        <Alert severity="info">
          One report per student will be emailed to their parent
          {recipientCount ? ` — ${recipientCount} enrolled` : ""}. Students who already had a
          report for this exact period are skipped.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending} color="inherit">
          Cancel
        </Button>
        <Button onClick={send} disabled={sending} variant="contained" startIcon={<SendIcon />}>
          {sending ? "Queueing…" : "Send to all parents"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupProgressReportDialog;
